import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ref, getBytes, uploadBytes } from 'firebase/storage';
import { kbStorage } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Breadcrumbs,
  Link,
  Paper,
  Divider,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DirectoryPane from './DirectoryPane';

const ROOT_FILE = 'README.md';

/**
 * Resolve a relative href against the current document path within the bucket.
 * e.g. currentPath='appendices/overview.md', href='../sub-seasons.md' → 'sub-seasons.md'
 */
function resolveRelativePath(currentPath, href) {
  // Absolute URLs pass through unchanged
  if (/^https?:\/\//i.test(href)) return href;

  const base = currentPath.includes('/')
    ? currentPath.substring(0, currentPath.lastIndexOf('/') + 1)
    : '';

  // Use URL API for robust relative resolution (dummy origin)
  try {
    const resolved = new URL(href, `https://x/${base}`);
    // Strip the leading slash from pathname
    return resolved.pathname.replace(/^\//, '');
  } catch {
    return href;
  }
}

const READONLY_PREFIX = 'youtube-videos/';

export default function KnowledgeBase() {
  const { isAuthorized } = useAuth();
  const canEdit = isAuthorized(['Admin']);

  const [paneOpen, setPaneOpen] = useState(true);

  const [currentPath, setCurrentPath] = useState(ROOT_FILE);
  const [history, setHistory] = useState([]);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const contentRef = useRef(null);

  // Reset scroll to top whenever the displayed document changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [currentPath]);

  const fetchDocument = useCallback(async (path) => {
    setLoading(true);
    setError(null);
    setIsEditing(false);
    setEditContent('');
    setSaveError(null);
    try {
      const bytes = await getBytes(ref(kbStorage, path));
      const text = new TextDecoder('utf-8').decode(bytes);
      setContent(text);
      setCurrentPath(path);
    } catch (err) {
      console.error('KnowledgeBase fetch error:', err);
      setError(`Could not load "${path}". The file may not exist or you may not have permission to read it.`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocument(ROOT_FILE);
  }, [fetchDocument]);

  const navigateTo = useCallback((path) => {
    setHistory((prev) => [...prev, currentPath]);
    fetchDocument(path);
  }, [currentPath, fetchDocument]);

  const navigateBack = useCallback(() => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    fetchDocument(prev);
  }, [history, fetchDocument]);

  const handleEdit = useCallback(() => {
    setEditContent(content);
    setSaveError(null);
    setIsEditing(true);
  }, [content]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditContent('');
    setSaveError(null);
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const bytes = new TextEncoder().encode(editContent);
      await uploadBytes(ref(kbStorage, currentPath), bytes, { contentType: 'text/plain; charset=utf-8' });
      setContent(editContent);
      setIsEditing(false);
      setEditContent('');
    } catch (err) {
      console.error('KnowledgeBase save error:', err);
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [editContent, currentPath]);

  // Custom anchor renderer — intercept .md links, open others in new tab
  // Destructure `node` to prevent react-markdown from forwarding the AST node to the DOM
  const anchorRenderer = useCallback(({ href, children, node, ...props }) => {
    if (!href) return <span {...props}>{children}</span>;

    const isExternal = /^https?:\/\//i.test(href);
    const isHash = href.startsWith('#');

    if (!isExternal && href.endsWith('.md')) {
      const resolved = resolveRelativePath(currentPath, href);
      return (
        <button
          type="button"
          onClick={() => navigateTo(resolved)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            color: '#1976d2',
            textDecoration: 'underline',
            cursor: 'pointer',
            font: 'inherit',
            verticalAlign: 'baseline',
            textAlign: 'left',
          }}
        >
          {children}
        </button>
      );
    }

    // Hash anchors scroll within the current rendered content — no new tab
    if (isHash) {
      return <a href={href} {...props}>{children}</a>;
    }

    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    );
  }, [currentPath, navigateTo]);

  const isRoot = currentPath === ROOT_FILE;

  const markdownComponents = {
    a: anchorRenderer,
    h1: ({ node, ...props }) => <Typography variant="h5" sx={{ mt: 3, mb: 1.5, fontWeight: 700 }} {...props} />,
    h2: ({ node, ...props }) => <Typography variant="h6" sx={{ mt: 2.5, mb: 1, fontWeight: 600 }} {...props} />,
    h3: ({ node, ...props }) => <Typography variant="body1" sx={{ mt: 2, mb: 0.5, fontWeight: 600, fontSize: '1.05rem' }} {...props} />,
    p: ({ node, ...props }) => <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.7, textAlign: 'left' }} {...props} />,
    hr: () => <Divider sx={{ my: 2 }} />,
    blockquote: ({ node, ...props }) => (
      <Box
        component="blockquote"
        sx={{ borderLeft: '4px solid', borderColor: 'primary.main', pl: 2, ml: 0, my: 1.5, color: 'text.secondary' }}
        {...props}
      />
    ),
    code: ({ node, className, children, ...props }) => (
      className ? (
        <Box
          component="pre"
          sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, overflow: 'auto', my: 1.5, fontSize: '0.875rem', fontFamily: 'monospace' }}
        >
          <code className={className} {...props}>{children}</code>
        </Box>
      ) : (
        <Box
          component="code"
          sx={{ bgcolor: 'grey.100', px: 0.75, py: 0.25, borderRadius: 0.5, fontSize: '0.875em', fontFamily: 'monospace', whiteSpace: 'nowrap' }}
          {...props}
        >{children}</Box>
      )
    ),
    table: ({ node, ...props }) => (
      <Box sx={{ overflowX: 'auto', my: 2 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.9rem' }} {...props} />
      </Box>
    ),
    thead: ({ node, ...props }) => <thead style={{ backgroundColor: '#f5f5f5' }} {...props} />,
    th: ({ node, ...props }) => (
      <th style={{ border: '1px solid #e0e0e0', padding: '10px 14px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }} {...props} />
    ),
    td: ({ node, ...props }) => (
      <td style={{ border: '1px solid #e0e0e0', padding: '8px 14px', verticalAlign: 'top' }} {...props} />
    ),
    tr: ({ node, ...props }) => (
      <tr style={{ borderBottom: '1px solid #e0e0e0' }} {...props} />
    ),
    ul: ({ node, ...props }) => <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem', lineHeight: 1.8 }} {...props} />,
    ol: ({ node, ...props }) => <ol style={{ paddingLeft: '1.5rem', marginBottom: '1rem', lineHeight: 1.8 }} {...props} />,
    li: ({ node, ...props }) => <li style={{ marginBottom: '0.25rem' }} {...props} />,
  };

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden', minHeight: 0 }}>
      {/* ── Directory pane ── */}
      <Box
        sx={{
          width: paneOpen ? 280 : 0,
          minWidth: paneOpen ? 280 : 0,
          transition: 'width 0.2s ease, min-width 0.2s ease',
          overflow: 'hidden',
          borderRight: paneOpen ? '1px solid' : 'none',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <DirectoryPane
          currentPath={currentPath}
          onFileSelect={navigateTo}
          onFileDeleted={(path) => { if (path === currentPath) navigateTo('README.md'); }}
          canEdit={canEdit}
        />
      </Box>

      {/* ── Main content column ── */}
      <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Tooltip title={paneOpen ? 'Hide file tree' : 'Show file tree'}>
          <IconButton size="small" onClick={() => setPaneOpen((v) => !v)} sx={{ mr: 0.5 }}>
            {paneOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Tooltip>
        <MenuBookIcon color="primary" />
        <Typography variant="h5" component="h1" sx={{ flex: 1 }}>
          Knowledge Base
        </Typography>
        {!loading && !error && canEdit && !currentPath.startsWith(READONLY_PREFIX) && (
          isEditing ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                size="small"
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={saving}
                size="small"
              >
                Cancel
              </Button>
            </Box>
          ) : (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              size="small"
            >
              Edit
            </Button>
          )
        )}
      </Box>

      {/* Breadcrumb / Back navigation */}
      {!isRoot && (
        <Box sx={{ mb: 2 }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              component="button"
              underline="hover"
              color="inherit"
              onClick={() => navigateTo(ROOT_FILE)}
            >
              README
            </Link>
            {history.slice(1).map((p, i) => (
              <Link
                key={p}
                component="button"
                underline="hover"
                color="inherit"
                onClick={() => {
                  // Navigate back to that point in history
                  const targetPath = history[i + 1];
                  const newHistory = history.slice(0, i + 1);
                  setHistory(newHistory);
                  fetchDocument(targetPath || p);
                }}
              >
                {p.split('/').pop()}
              </Link>
            ))}
            <Typography color="text.primary">{currentPath.split('/').pop()}</Typography>
          </Breadcrumbs>
          <Button
            startIcon={<ArrowBackIcon />}
            size="small"
            onClick={navigateBack}
            sx={{ mt: 1 }}
          >
            Back
          </Button>
        </Box>
      )}

      {/* Save error */}
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      {/* Content area — split-screen editor or read-only view */}
      {isEditing ? (
        <Box sx={{ display: 'flex', flex: 1, gap: 2, minHeight: 0 }}>
          {/* Editor pane */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
              Markdown
            </Typography>
            <TextField
              multiline
              fullWidth
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              variant="outlined"
              inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.875rem', lineHeight: 1.6 } }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': { height: '100%', alignItems: 'flex-start' },
                '& .MuiInputBase-input': { height: '100% !important', overflow: 'auto !important' },
              }}
            />
          </Box>

          {/* Preview pane */}
          <Paper variant="outlined" sx={{ flex: 1, p: 2, overflow: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Preview
            </Typography>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {editContent}
            </ReactMarkdown>
          </Paper>
        </Box>
      ) : (
        <Paper ref={contentRef} variant="outlined" sx={{ p: 3, flex: 1, overflow: 'auto' }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && error && (
            <Alert severity="error">{error}</Alert>
          )}

          {!loading && !error && (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {content}
            </ReactMarkdown>
          )}
        </Paper>
      )}
      </Box>
    </Box>
  );
}
